import { buildHiddenContextBlock, createOptimisticUserMessage, toChatError } from './utils/chatHelpers';
import { Metrics } from './chatMetrics';
import { isArborHost } from './isArborHost';
import { ToolUseChirpContentBlockTypename, ServerToolUseChirpContentBlockTypename, ContextChirpContentBlockTypename, TextChirpContentBlockTypename } from '../../__generated__/chirp/com/hubspot/breeze/engine/rpc/BreezeEngineStreamingService';
import { matchesNegativeKeyword } from './utils/matchesNegativeKeyword';
import { getConversationRpc } from '../../__generated__/chirp/com/hubspot/breeze/engine/rpc/BreezeEngineConversationsService';
import { createToolResolver } from './coordinator/toolResolver';
import { createConversationLoader } from './coordinator/conversationLoader';
import { createConversationHistory } from './conversationHistory';
import { createDefaultChirpClient } from './chirpClient';
import { createStreamingBreezeConnection } from './streaming/streamingConnection';
import { createPollingBreezeConnection } from './polling/pollingConnection';
import { createAblyBreezeConnection } from './ably/ablyConnection';
import { createStreamingTransport } from './streaming/streamingTransport';
import { createPollingTransport } from './polling/pollingTransport';
import { createAblyTransport } from './ably/ablyTransport';
import { chatReducer } from './reducer/chatReducer';
import { getInitialState } from './reducer/types';
import { breezeEngineRegistry } from '../sdk/BreezeEngineRegistry';
import { createChatLogger } from './logger';
import { createChatTelemetry, noopTelemetry } from './chatTelemetry';
// Cap queued messages to avoid unbounded buffering while the client is busy.
// 3 strikes a balance between "user can type ahead" and "don't hide a wall of
// unsent text that fires off unexpectedly when the turn completes."
const MAX_QUEUE_DEPTH = 3;
const TITLE_POLL_INTERVAL_MS = 2000;
const TITLE_POLL_MAX_ATTEMPTS = 15;
export function createChatClient(options) {
  var _options$__onInit;
  const {
    transport,
    agentId: initialAgentId,
    conversationHistory,
    conversationsChirpClient,
    pollConversationTitle = true,
    onError: onErrorCallback,
    onConversationCreated: onConversationCreatedCallback,
    onStatusChange: onStatusChangeCallback,
    onStreamEvent: onStreamEventCallback,
    onTitleChanged: onTitleChangedCallback,
    registry = breezeEngineRegistry,
    logger = createChatLogger(),
    telemetry = noopTelemetry
  } = options;
  const metricsEnabled = !isArborHost();
  let state = getInitialState({
    defaultAgentId: initialAgentId,
    agentId: initialAgentId
  });
  let toolMap = new Map();
  let cachedToolSpecs = [];
  let cachedSkillSpecs = [];
  let cachedSubagentSpecs = [];
  let cachedSnapshot = null;
  // Per-send `messageContexts` from the turn that is currently in flight. The
  // FE only attaches these on the initiating `sendMessage` (e.g. a journey's
  // SkillsContext), but a turn paused by `ask_user_question` resumes via
  // `sendToolResponse`, which has no per-call sendOptions. Without replaying
  // them the backend rebuilds the agent with no SkillsContext and silently
  // drops registry skills + their tools. Captured here so the resume path can
  // re-send them for the same turnId.
  let pendingSendMessageContexts = [];
  let dirty = true;
  let drainingQueue = false;
  const listeners = new Set();
  let titlePollGeneration = 0;
  async function pollForTitle(conversationId) {
    if (!conversationsChirpClient || !pollConversationTitle) return;
    const generation = ++titlePollGeneration;
    let attempts = 0;
    while (attempts < TITLE_POLL_MAX_ATTEMPTS) {
      if (state.title !== null || state.error !== null || titlePollGeneration !== generation) return;
      await new Promise(resolve => setTimeout(resolve, TITLE_POLL_INTERVAL_MS));
      if (state.title !== null || state.error !== null || titlePollGeneration !== generation) return;
      try {
        const response = await conversationsChirpClient.call(getConversationRpc, {
          request: {
            conversationId
          }
        });
        // re-check after the async call; generation may have changed while in-flight
        if (titlePollGeneration !== generation) return;
        if (response.title) {
          dispatch({
            type: 'TITLE_UPDATED',
            title: response.title
          });
          onTitleChangedCallback === null || onTitleChangedCallback === void 0 || onTitleChangedCallback(conversationId, response.title);
          return;
        }
      } catch (_unused) {
        // ignore, retry on next interval
      }
      attempts++;
    }
  }
  function dispatch(action) {
    const prev = state;
    state = chatReducer(prev, action);
    if (state === prev) {
      logger.dispatchNoop(action);
      return;
    }
    logger.dispatch(action, prev, state);
    // Edge-trigger on the transition into READY so redundant TURN_COMPLETED
    // dispatches (e.g. external-tool channel death + final END_TURN) count once.
    if (metricsEnabled && action.type === 'TURN_COMPLETED' && prev.status !== 'READY') {
      telemetry.turnCompleted();
    }
    dirty = true;
    notify();
    if (prev.status !== state.status) {
      onStatusChangeCallback === null || onStatusChangeCallback === void 0 || onStatusChangeCallback(state.status, prev.status);
    }
    if (prev.conversationId === null && state.conversationId !== null) {
      onConversationCreatedCallback === null || onConversationCreatedCallback === void 0 || onConversationCreatedCallback(state.conversationId);
    }
    if (state.status === 'ERROR' && prev.status !== 'ERROR' && state.error) {
      onErrorCallback === null || onErrorCallback === void 0 || onErrorCallback(state.error);
    }
    if (prev.status !== 'READY' && state.status === 'READY') {
      if (state.messageQueue.length > 0 && !drainingQueue) {
        void drainQueue();
      }
    }
  }
  (_options$__onInit = options.__onInit) === null || _options$__onInit === void 0 || _options$__onInit.call(options, {
    dispatch
  });
  function rebuildToolSpecs() {
    cachedToolSpecs = [...toolMap.values()].map(t => ({
      name: t.name,
      description: t.description,
      inputSchema: JSON.stringify(t.inputSchema)
    }));
  }
  if (options.tools) {
    toolMap = new Map(options.tools.map(t => [t.name, t]));
    rebuildToolSpecs();
  }
  logger.recordingStart({
    agentId: initialAgentId,
    conversationId: null,
    messages: [],
    messageTurnIds: [],
    tools: [...toolMap.values()].map(t => ({
      name: t.name,
      description: t.description,
      inputSchema: t.inputSchema
    }))
  });
  function getMergedToolSpecs() {
    const registrySpecs = registry.getExternalToolSpecs();
    const localNames = new Set(cachedToolSpecs.map(spec => spec.name));
    const filtered = registrySpecs.filter(spec => !localNames.has(spec.name));
    return [...cachedToolSpecs, ...filtered];
  }
  function getMergedSkillSpecs() {
    const registrySpecs = registry.getRegistrySkillSpecs();
    const localNames = new Set(cachedSkillSpecs.map(spec => spec.name));
    const filtered = registrySpecs.filter(spec => !localNames.has(spec.name));
    return [...cachedSkillSpecs, ...filtered];
  }
  function resolveMetadataTarget() {
    return state.conversationId != null ? 'turnMetadata' : 'conversationMetadata';
  }
  function getRegistryConversationMetadata() {
    const items = registry.getConversationMetadata();
    if (items.length === 0) return {};
    const metadata = {};
    for (const item of items) {
      if (item.value === null || item.value === undefined) continue;
      metadata[item.contextKey] = typeof item.value === 'object' ? JSON.stringify(item.value) : String(item.value);
    }
    return metadata;
  }
  function buildSendMetadata(sendOptions) {
    const target = resolveMetadataTarget();
    const registryMeta = getRegistryConversationMetadata();
    let turn;
    let conversation;
    if (target === 'turnMetadata') {
      turn = Object.assign({}, registryMeta, sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.turnMetadata);
      conversation = Object.assign({}, sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.conversationMetadata);
    } else {
      turn = Object.assign({}, sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.turnMetadata);
      conversation = Object.assign({}, registryMeta, sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.conversationMetadata);
    }
    return Object.assign({}, Object.keys(turn).length > 0 && {
      turnMetadata: turn
    }, Object.keys(conversation).length > 0 && {
      conversationMetadata: conversation
    });
  }
  function buildSendContexts(sendOptions) {
    var _sendOptions$messageC, _sendOptions$fileRefe;
    const messageContexts = [...registry.getMessageContexts(), ...((_sendOptions$messageC = sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.messageContexts) !== null && _sendOptions$messageC !== void 0 ? _sendOptions$messageC : [])];
    return Object.assign({}, messageContexts.length > 0 && {
      messageContexts
    }, (sendOptions === null || sendOptions === void 0 || (_sendOptions$fileRefe = sendOptions.fileReferences) === null || _sendOptions$fileRefe === void 0 ? void 0 : _sendOptions$fileRefe.length) && {
      fileReferences: sendOptions.fileReferences
    });
  }

  // Build everything in the transport request except the message payload itself
  // -- agent identity, conversation tracking, metadata, tools/skills, contexts,
  // and file references. Used by both the regular send path and the queue
  // drain path; the caller adds the `message` field (a string or a content-
  // block array) on top.
  function buildBaseSendRequest(sendOptions) {
    return Object.assign({
      agentId: state.agentId
    }, state.conversationId ? {
      conversationId: state.conversationId
    } : {}, sendOptions !== null && sendOptions !== void 0 && sendOptions.collectionId ? {
      collectionId: sendOptions.collectionId
    } : {}, (sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.configVersion) != null ? {
      configVersion: sendOptions.configVersion
    } : {}, buildSendMetadata(sendOptions), {
      externalToolSpecs: getMergedToolSpecs(),
      externalSkillSpecs: getMergedSkillSpecs(),
      externalSubagentSpecs: cachedSubagentSpecs.length > 0 ? cachedSubagentSpecs : undefined
    }, buildSendContexts(sendOptions));
  }

  // Merge per-call `sendOptions.context` with the registry's running context.
  // The string-message path prepends the result as a hidden `<context>` block
  // so the agent can see it without it appearing in the chat UI.
  function getEffectiveContext(sendOptions) {
    const registryCtx = registry.getContext();
    const callerCtx = sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.context;
    if (callerCtx === undefined) return registryCtx;
    return Object.assign({}, registryCtx, callerCtx);
  }
  function notify() {
    for (const listener of listeners) {
      listener();
    }
  }
  const clientCtx = {
    getState: () => state,
    dispatch,
    transport,
    logger,
    telemetry,
    createHandler,
    getMergedToolSpecs,
    getExternalSkillSpecs: () => getMergedSkillSpecs(),
    getExternalSubagentSpecs: () => cachedSubagentSpecs,
    // On resume there are no per-call sendOptions, so this replays the current
    // registry context. conversationId is set, so buildSendMetadata routes
    // registry metadata to turnMetadata; conversationMetadata/fileReferences are
    // dropped since the backend persists/re-fetches those on resume.
    getResumeMetadata: () => {
      const {
        turnMetadata
      } = buildSendMetadata(undefined);
      return turnMetadata ? {
        turnMetadata
      } : {};
    },
    getResumeContexts: () => {
      const {
        messageContexts: registryContexts
      } = buildSendContexts(undefined);
      const messageContexts = [...(registryContexts !== null && registryContexts !== void 0 ? registryContexts : []), ...pendingSendMessageContexts];
      return messageContexts.length > 0 ? {
        messageContexts
      } : {};
    }
  };
  const toolResolver = createToolResolver(clientCtx, () => toolMap, registry);
  const conversationLoader = createConversationLoader(clientCtx, conversationHistory, {
    onError: onErrorCallback,
    onPendingToolsFound: () => toolResolver.handleExternalTool(),
    onPendingApprovalFound: () => toolResolver.handleToolApproval()
  });
  function createHandler() {
    return {
      onStreamEvent(event) {
        onStreamEventCallback === null || onStreamEventCallback === void 0 || onStreamEventCallback(event);
      },
      onTurnStarted(cId, tId, title) {
        // isFirstTurn covers two cases:
        // 1. Brand new conversation (conversationId was null)
        // 2. First real message in a loaded conversation (loadConversation sets
        //    turnId=0 as a sentinel; the real first turn has a non-zero turnId)
        const isFirstTurn = state.conversationId === null || state.turnId === 0;
        dispatch({
          type: 'TURN_STARTED',
          conversationId: cId,
          turnId: tId,
          title
        });
        if (title !== undefined) {
          onTitleChangedCallback === null || onTitleChangedCallback === void 0 || onTitleChangedCallback(cId, title);
        }
        if (isFirstTurn && state.title === null) {
          void pollForTitle(state.conversationId);
        }
      },
      onMessageUpdate(update) {
        switch (update.type) {
          case 'append':
            {
              // Skip metric emission when the reducer would dedupe this append
              // (resume/recovery streams replay history before delivering new
              // events). Counting here would double-count tool-invoked even
              // though the duplicate MESSAGE_APPEND is a no-op in state.
              const isNewMessage = !state.messages.some(existing => existing.id === update.message.id);
              if (isNewMessage && Array.isArray(update.message.content)) {
                for (const block of update.message.content) {
                  if (metricsEnabled && (block.__typename === ToolUseChirpContentBlockTypename || block.__typename === ServerToolUseChirpContentBlockTypename)) {
                    Metrics.counter('tool-invoked', {
                      tool_name: block.name,
                      agent_id: state.agentId
                    }).increment();
                  }
                }
              }
              dispatch({
                type: 'MESSAGE_APPEND',
                message: update.message,
                keepDeltas: update.stopReason === 'CANCELLED'
              });
              break;
            }
          case 'delta-update':
            dispatch({
              type: 'MESSAGE_DELTA_UPDATE',
              message: update.message
            });
            break;
          case 'replace-optimistic':
            dispatch({
              type: 'MESSAGE_REPLACE_OPTIMISTIC',
              message: update.message
            });
            break;
          case 'replace-all':
            dispatch({
              type: 'MESSAGE_REPLACE_ALL',
              messages: update.messages
            });
            break;
          default:
            return;
        }
      },
      async onStopReason(stopReason) {
        await toolResolver.handleStopReason(stopReason);
      },
      onComplete() {
        dispatch({
          type: 'TURN_COMPLETED'
        });
      },
      onError(err) {
        dispatch({
          type: 'MESSAGE_REMOVE_LAST_OPTIMISTIC'
        });
        dispatch({
          type: 'TURN_ERROR',
          error: toChatError(err)
        });
      },
      onReconnecting() {
        dispatch({
          type: 'RECONNECTING_SET'
        });
      }
    };
  }
  async function drainQueue() {
    if (state.messageQueue.length === 0 || drainingQueue) return;
    drainingQueue = true;
    const next = state.messageQueue[0];
    dispatch({
      type: 'QUEUE_DEQUEUE'
    });
    const registryCtx = registry.getContext();
    const effectiveContent = Object.keys(registryCtx).length > 0 ? `${buildHiddenContextBlock(registryCtx)}${next.content}` : next.content;
    const optimisticMsg = createOptimisticUserMessage(effectiveContent);
    dispatch({
      type: 'MESSAGE_APPEND',
      message: optimisticMsg
    });
    dispatch({
      type: 'STATUS_SET',
      status: transport.activeStatus
    });
    pendingSendMessageContexts = [];
    drainingQueue = false;
    try {
      await transport.send(Object.assign({
        agentId: state.agentId
      }, state.conversationId ? {
        conversationId: state.conversationId
      } : {}, {
        message: effectiveContent
      }, buildSendMetadata(undefined), {
        externalToolSpecs: getMergedToolSpecs(),
        externalSkillSpecs: getMergedSkillSpecs(),
        externalSubagentSpecs: cachedSubagentSpecs.length > 0 ? cachedSubagentSpecs : undefined
      }, buildSendContexts(undefined)), createHandler());
    } catch (_unused2) {
      dispatch({
        type: 'QUEUE_RESTORE_FAILED',
        item: next
      });
      dispatch({
        type: 'MESSAGE_REMOVE_LAST_OPTIMISTIC'
      });
    }
  }

  // Shared tail for both `sendMessage` and `sendMessages` once gating is done:
  // record the message, set status to streaming, build the send request, and
  // hand off to the transport. `optimisticContent` is what the user sees in
  // their bubble; `wireContent` is what goes on the wire (may include the
  // hidden-context prefix that the string path adds).
  async function executeSend(optimisticContent, wireContent, sendOptions) {
    var _sendOptions$messageC2;
    if (metricsEnabled && !state.conversationId) {
      Metrics.counter('chat-session-started', {
        agent_id: state.agentId
      }).increment();
    }
    if (!(sendOptions !== null && sendOptions !== void 0 && sendOptions.hidden)) {
      const messageText = typeof optimisticContent === 'string' ? optimisticContent : optimisticContent.filter(block => block.__typename === TextChirpContentBlockTypename).map(block => 'text' in block ? block.text : '').join(' ');
      if (metricsEnabled) {
        Metrics.counter('message-sent', {
          agent_id: state.agentId,
          has_negative_keyword: String(matchesNegativeKeyword(messageText))
        }).increment();
      }
      dispatch({
        type: 'MESSAGE_APPEND',
        message: createOptimisticUserMessage(optimisticContent, sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.fileReferences)
      });
    }
    dispatch({
      type: 'STATUS_SET',
      status: transport.activeStatus
    });
    pendingSendMessageContexts = (_sendOptions$messageC2 = sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.messageContexts) !== null && _sendOptions$messageC2 !== void 0 ? _sendOptions$messageC2 : [];
    const sendRequest = Object.assign({}, buildBaseSendRequest(sendOptions), {
      message: wireContent
    }, (sendOptions === null || sendOptions === void 0 ? void 0 : sendOptions.hidden) && {
      skipUserEcho: true
    });
    logger.transportSend(sendRequest);
    await transport.send(sendRequest, createHandler());
  }
  function clear() {
    titlePollGeneration++;
    pendingSendMessageContexts = [];
    transport.clear();
    dispatch({
      type: 'RESET'
    });
  }
  async function cancelActiveTurn(cId, tId) {
    if (cId !== null && tId !== null && transport.isPersistentChannel) {
      // Persistent-channel (Ably): stay subscribed and keep the input locked
      // until the interrupt (stopReason: CANCELLED) arrives and drives
      // TURN_COMPLETED, so the user can't send before it's shown.
      dispatch({
        type: 'STATUS_SET',
        status: 'STREAMING'
      });
      await transport.cancelTurn({
        conversationId: cId,
        turnId: tId,
        reason: 'User cancelled'
      });
      return;
    }
    // Non-persistent (streaming/polling): tear down locally and go READY now.
    transport.stop();
    dispatch({
      type: 'STATUS_SET',
      status: 'READY'
    });
    if (cId !== null && tId !== null) {
      await transport.cancelTurn({
        conversationId: cId,
        turnId: tId,
        reason: 'User cancelled'
      });
    }
  }
  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot() {
      if (!dirty && cachedSnapshot) return cachedSnapshot;
      cachedSnapshot = {
        messages: state.messages,
        messageTurnIds: state.messageTurnIdMap,
        feedbackByTurnId: state.feedbackByTurnId,
        feedbackByCitationKey: state.feedbackByCitationKey,
        status: state.status,
        conversationId: state.conversationId,
        turnId: state.turnId,
        title: state.title,
        error: state.error,
        activeTools: state.activeTools,
        queuedMessages: state.messageQueue,
        agentId: state.agentId,
        isReconnecting: state.isReconnecting,
        history: {
          hasMore: state.historyHasMore,
          isLoadingMore: state.isLoadingMore
        }
      };
      dirty = false;
      return cachedSnapshot;
    },
    setTools(tools) {
      toolMap = new Map(tools.map(t => [t.name, t]));
      rebuildToolSpecs();
    },
    setExternalSkillSpecs(specs) {
      cachedSkillSpecs = specs;
    },
    setExternalSubagentSpecs(specs) {
      cachedSubagentSpecs = specs;
    },
    async resumeTurn(resumeTurnId, lastMessageId) {
      if (state.conversationId === null || state.status !== 'READY') return;
      const conversationId = state.conversationId;
      dispatch({
        type: 'TURN_STARTED',
        conversationId,
        turnId: resumeTurnId
      });
      dispatch({
        type: 'STATUS_SET',
        status: transport.activeStatus
      });
      await transport.resume(Object.assign({
        conversationId,
        turnId: resumeTurnId
      }, lastMessageId != null ? {
        lastMessageId
      } : {}), createHandler());
    },
    async sendMessage(content, sendOptions) {
      var _sendOptions$fileRefe2;
      if (state.status === 'LOADING') return;
      const isString = typeof content === 'string';
      const isEmpty = isString ? content.trim().length === 0 : content.length === 0;
      // Allow file-only messages — fileReferences are valid content even without text
      if (isEmpty && !(sendOptions !== null && sendOptions !== void 0 && (_sendOptions$fileRefe2 = sendOptions.fileReferences) !== null && _sendOptions$fileRefe2 !== void 0 && _sendOptions$fileRefe2.length)) return;
      if (sendOptions !== null && sendOptions !== void 0 && sendOptions.hidden) {
        if (state.status !== 'READY') return;
        const wireContent = isString ? [{
          __typename: ContextChirpContentBlockTypename,
          text: content
        }] : content;
        await executeSend(content, wireContent, sendOptions);
        return;
      }
      if (state.status !== 'READY') {
        if (state.status === 'ERROR' || state.messageQueue.length >= MAX_QUEUE_DEPTH) return;
        // The reducer's queue stores string content; block-shaped sends are
        // dropped (callers gate on `status === 'READY'`). Widening the queue
        // would require a discriminated-union QueuedMessage + reducer changes.
        if (isString && !isEmpty) {
          dispatch({
            type: 'QUEUE_ENQUEUE',
            content
          });
        }
        return;
      }
      // Hidden-context injection only applies to the string path -- there's no
      // canonical splice point in a block array, so block callers own their
      // content shape entirely.
      let wireContent = content;
      if (isString) {
        const ctx = getEffectiveContext(sendOptions);
        if (Object.keys(ctx).length > 0) {
          wireContent = `${buildHiddenContextBlock(ctx)}${content}`;
        }
      }
      await executeSend(content, wireContent, sendOptions);
    },
    addToolResult(toolUseId, result) {
      const toolCall = state.activeTools.find(t => t.toolUseId === toolUseId);
      if (!toolCall || toolCall.status === 'complete' || toolCall.status === 'error') return;
      const toolResult = {
        content: result.content
      };
      dispatch({
        type: 'TOOL_RESULT_SET',
        toolUseId,
        result: result.isError ? {
          type: 'TOOL_ERROR',
          message: result.content
        } : toolResult,
        status: 'complete'
      });
      toolResolver.checkAndSendToolResponse().catch(err => {
        dispatch({
          type: 'TURN_ERROR',
          error: toChatError(err, 'Failed to send tool response')
        });
      });
    },
    approveTool(toolUseId, {
      approved,
      denialFeedback
    }) {
      void toolResolver.resolveToolApproval(toolUseId, approved, denialFeedback);
    },
    removeQueuedMessage(id) {
      dispatch({
        type: 'QUEUE_REMOVE',
        id
      });
    },
    async stop() {
      switch (state.status) {
        case 'READY':
        case 'ERROR':
          if (state.messageQueue.length > 0) {
            dispatch({
              type: 'QUEUE_CLEAR'
            });
          }
          return;
        case 'LOADING':
          {
            dispatch({
              type: 'HISTORY_INCREMENT_FETCH_ID'
            });
            dispatch({
              type: 'RESET_TURN'
            });
            dispatch({
              type: 'QUEUE_CLEAR'
            });
            dispatch({
              type: 'STATUS_SET',
              status: 'READY'
            });
            return;
          }
        case 'STREAMING':
          {
            const cId = state.conversationId;
            const tId = state.turnId;
            pendingSendMessageContexts = [];
            dispatch({
              type: 'QUEUE_CLEAR'
            });
            await cancelActiveTurn(cId, tId);
            return;
          }
        case 'AWAITING_TOOL':
        case 'AWAITING_APPROVAL':
          {
            const cId = state.conversationId;
            const tId = state.turnId;
            pendingSendMessageContexts = [];
            dispatch({
              type: 'CANCEL_MARK'
            });
            dispatch({
              type: 'TOOL_CLEAR'
            });
            dispatch({
              type: 'QUEUE_CLEAR'
            });
            dispatch({
              type: 'CANCEL_CLEAR'
            });
            toolResolver.cancelPendingApproval();
            await cancelActiveTurn(cId, tId);
            return;
          }
        default:
          return;
      }
    },
    clear,
    setAgentId(newAgentId) {
      if (newAgentId === state.agentId) return;
      clear();
      dispatch({
        type: 'AGENT_ID_SET',
        agentId: newAgentId
      });
    },
    destroy() {
      titlePollGeneration++;
      logger.clientDestroyed();
      transport.stop();
      transport.clear();
    },
    loadConversation: id => conversationLoader.loadConversation(id),
    loadMore: () => conversationLoader.loadMore(),
    injectMessage(message) {
      if (state.status !== 'READY') return;
      dispatch({
        type: 'MESSAGE_APPEND',
        message
      });
    },
    async submitFeedback(opts) {
      var _opts$turnId, _opts$tags;
      const {
        citationId,
        removed
      } = opts;
      const targetTurnId = (_opts$turnId = opts.turnId) !== null && _opts$turnId !== void 0 ? _opts$turnId : state.turnId;
      if (state.conversationId === null || !targetTurnId) return;
      await transport.submitFeedback(Object.assign({
        conversationId: state.conversationId,
        turnId: targetTurnId,
        feedbackType: opts.type
      }, opts.text ? {
        feedbackText: opts.text
      } : {}, {
        tags: (_opts$tags = opts.tags) !== null && _opts$tags !== void 0 ? _opts$tags : []
      }, citationId ? {
        citationId
      } : {}, removed ? {
        removed
      } : {}));
      if (removed) {
        dispatch(Object.assign({
          type: 'FEEDBACK_REMOVED',
          turnId: targetTurnId
        }, citationId ? {
          citationId
        } : {}));
      } else {
        dispatch(Object.assign({
          type: 'FEEDBACK_SUBMITTED',
          turnId: targetTurnId,
          feedbackType: opts.type
        }, citationId ? {
          citationId
        } : {}));
      }
    }
  };
}
export function createClient(options) {
  var _options$mode, _options$logger, _options$conversation, _options$__conversati;
  const mode = (_options$mode = options.mode) !== null && _options$mode !== void 0 ? _options$mode : 'streaming';
  const logger = (_options$logger = options.logger) !== null && _options$logger !== void 0 ? _options$logger : createChatLogger();
  // Always-on metrics sink, kept separate from the logger and threaded through
  // the transports and coordinator (see ChatTelemetry).
  const telemetry = createChatTelemetry(mode);
  let transport;
  if (mode === 'ably') {
    var _options$__ablyConnec;
    const connection = (_options$__ablyConnec = options.__ablyConnection) !== null && _options$__ablyConnec !== void 0 ? _options$__ablyConnec : createAblyBreezeConnection({
      ablyClient: options.ablyClient,
      pollingChirpClient: options.pollingChirpClient,
      getAblyAuthToken: options.getAblyAuthToken,
      logger
    });
    transport = createAblyTransport(connection, logger, {}, telemetry);
  } else if (mode === 'polling') {
    var _options$__pollingCon;
    const connection = (_options$__pollingCon = options.__pollingConnection) !== null && _options$__pollingCon !== void 0 ? _options$__pollingCon : createPollingBreezeConnection(options.pollingChirpClient);
    transport = createPollingTransport(connection, logger, telemetry);
  } else {
    var _options$__streamingC;
    const connection = (_options$__streamingC = options.__streamingConnection) !== null && _options$__streamingC !== void 0 ? _options$__streamingC : createStreamingBreezeConnection(options.chirpClient);
    transport = createStreamingTransport(connection, logger, {}, telemetry);
  }
  const conversationsChirpClient = (_options$conversation = options.conversationsChirpClient) !== null && _options$conversation !== void 0 ? _options$conversation : createDefaultChirpClient();
  const conversationHistory = (_options$__conversati = options.__conversationHistory) !== null && _options$__conversati !== void 0 ? _options$__conversati : createConversationHistory(conversationsChirpClient);
  return createChatClient({
    transport,
    agentId: options.agentId,
    conversationHistory,
    conversationsChirpClient,
    pollConversationTitle: options.pollConversationTitle,
    logger,
    telemetry,
    tools: options.tools,
    __onInit: options.__onInit,
    onError: options.onError,
    onConversationCreated: options.onConversationCreated,
    onStatusChange: options.onStatusChange,
    onStreamEvent: options.onStreamEvent,
    onTitleChanged: options.onTitleChanged
  });
}